import { getNotifier } from '@doneverse/data/api'
import { useLeaveCall } from '@doneverse/data/hooks'
import type { CallRingEntry } from '@doneverse/shared'
import { Minus, Maximize2, PhoneOff } from 'lucide-react'
import { lazy, Suspense, useCallback, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { CallSeatWatcher } from './CallSeatWatcher'
import { Button } from '@/components/ui/button'
import { postMobileBridgeCallActive } from '@/lib/mobile-bridge'
import { useDashboardStore } from '@/store'

// The RealtimeKit SDK is a large bundle of Stencil web components. Loading it
// lazily keeps it out of the main chunk (and out of every test that isn't
// specifically about the meeting UI). E2E swaps this module for a stub via a
// vite.config.ts alias (the SDK can't connect under Playwright) — which is why
// the import must go through the `@/` alias rather than `./CallSurface`.
const CallSurface = lazy(() => import('@/components/calls/CallSurface'))

/**
 * What the dock header says while we're in a call.
 *
 * A call where you're the only one on the line looked exactly like a call in
 * progress, which is the single most confusing thing about the barebones
 * version: you couldn't tell "it's still ringing" from "they hung up" from
 * "they said no". The ring roster answers all three.
 */
function callStatusLabel(ring: CallRingEntry[], currentUserId: string | null, contactName: string): string {
  const others = ring.filter((entry) => entry.user_id !== currentUserId)

  const ringing = others.filter((entry) => entry.state === 'ringing')
  if (ringing.length === 1) return `Ringing ${ringing[0].name ?? contactName}…`
  if (ringing.length > 1) return `Ringing ${ringing.length} people…`

  // Nobody left to wait for. If everyone turned it down, say so rather than
  // leaving the caller staring at a normal-looking call.
  if (others.length > 0 && others.every((entry) => entry.state === 'declined')) {
    return others.length === 1 ? `${others[0].name ?? contactName} declined` : 'Declined'
  }

  return `Call · ${contactName}`
}

/**
 * Hosts the live call for the whole app.
 *
 * Mounted ONCE at the app root, outside the router — that's the entire point.
 * If this lived inside <ChatContent> the call would be torn down the moment you
 * navigated to another chat or another page, which is exactly the thing a
 * Slack-huddle-style call is supposed to survive.
 */
export function CallHost() {
  const activeCall = useDashboardStore((s) => s.activeCall)
  const clearActiveCall = useDashboardStore((s) => s.clearActiveCall)
  const setCallMinimized = useDashboardStore((s) => s.setCallMinimized)
  const currentUserId = useDashboardStore((s) => s.currentUserId)
  const leaveCall = useLeaveCall()

  // The native mobile shell holds a screen wake lock while a call is live —
  // otherwise the phone locks mid-call and suspends the WebRTC connection.
  // No-op outside the mobile WebView.
  const hasActiveCall = !!activeCall
  useEffect(() => {
    if (!hasActiveCall) return
    postMobileBridgeCallActive(true)
    return () => postMobileBridgeCallActive(false)
  }, [hasActiveCall])

  // The surface's meeting.leave(), registered once the meeting is live. Held in
  // a ref (not state) so registering it doesn't re-render the dock.
  const leaveMeetingRef = useRef<(() => Promise<void>) | null>(null)
  const registerLeave = useCallback((leave: (() => Promise<void>) | null) => {
    leaveMeetingRef.current = leave
  }, [])

  // Every exit from a call goes through here. Unmounting CallSurface does NOT
  // leave the meeting (see its `registerLeave` contract), so dropping the dock
  // without this leaves the microphone and the SFU connection live — stating
  // the invariant once is the only way it stays true for all three exits.
  const hangUpMedia = useCallback(() => {
    void leaveMeetingRef.current?.().catch(() => {})
    leaveMeetingRef.current = null
  }, [])

  // Drop the dock and tell the server. Reached from two places: after the
  // RealtimeKit UI reports roomLeft (media already down), and from handleLeave
  // (which hangs up first). Telling the server is best-effort — the reconciler
  // is the backstop if it never lands.
  const teardown = useCallback(() => {
    if (!activeCall) return
    const { callId } = activeCall
    clearActiveCall()
    leaveCall.mutate(callId)
  }, [activeCall, clearActiveCall, leaveCall])

  const handleLeave = useCallback(() => {
    hangUpMedia()
    teardown()
  }, [hangUpMedia, teardown])

  // Give the call up without reporting a leave. Two things set `releasing`:
  //
  //   handoff — the user joined on another device, which owns the seat now.
  //             Reporting a leave would try to vacate a seat that isn't ours
  //             (the server rejects it as stale, but not sending it keeps the
  //             two ends from disagreeing in the first place).
  //   ended   — the call is over, most often because a DM callee declined while
  //             we were still connected. There is nothing left to leave.
  const releasing = activeCall?.releasing ?? null
  useEffect(() => {
    if (!releasing) return
    hangUpMedia()
    clearActiveCall()
    if (releasing === 'handoff') {
      getNotifier().info?.('You joined this call on another device')
    }
  }, [releasing, hangUpMedia, clearActiveCall])

  if (!activeCall) return null

  const { minimized, contactName } = activeCall
  const statusLabel = callStatusLabel(activeCall.ring, currentUserId, contactName)

  return createPortal(
    <div
      className={
        minimized
          ? 'fixed bottom-4 right-4 z-50 w-[280px] rounded-xl border border-surface-border bg-white shadow-xl'
          : // Auto-height: the audio panel is a compact avatar dock, not a video grid.
            'fixed bottom-4 right-4 z-50 flex w-[340px] flex-col overflow-hidden rounded-xl border border-surface-border bg-white shadow-xl'
      }
      data-testid="call-dock"
    >
      <CallSeatWatcher activeCall={activeCall} />
      <div className="flex items-center justify-between gap-2 border-b border-surface-border px-3 py-2">
        <span
          className="truncate text-[13px] font-semibold text-black"
          title={contactName}
          data-testid="call-dock-status"
        >
          {statusLabel}
        </span>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            aria-label={minimized ? 'Expand call' : 'Minimize call'}
            onClick={() => setCallMinimized(!minimized)}
          >
            {minimized ? <Maximize2 className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-red-600 hover:text-red-700"
            aria-label="Leave call"
            onClick={handleLeave}
          >
            <PhoneOff className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/*
        The surface stays MOUNTED when minimized — just visually collapsed.
        Unmounting it would drop the WebRTC connection, i.e. minimizing would
        silently hang up on everyone.
      */}
      <div className={minimized ? 'h-0 overflow-hidden' : 'min-h-0'}>
        <Suspense fallback={<div className="p-4 text-sm text-muted-foreground">Connecting…</div>}>
          <CallSurface authToken={activeCall.authToken} onLeave={teardown} registerLeave={registerLeave} />
        </Suspense>
      </div>
    </div>,
    document.body,
  )
}
